import type {
  DeviceConfigurationSchema,
  DeviceConfigurationLayout,
  ConfigurationSection,
  ConfigurationProperty,
  DeviceConfigurationValues,
  JSONSchemaProperty,
  JSONSchemaObject
} from '../types/deviceConfiguration'

// Helper to get nested property from schema using dot notation path
function getSchemaProperty(schema: any, path: string): JSONSchemaProperty | null {
  const parts = path.split('.')
  let current = schema.properties

  for (const part of parts) {
    if (!current || !current[part]) {
      return null
    }
    current = current[part].properties || current[part]
  }

  return current as JSONSchemaProperty
}

// Determine field type from JSON schema type
function getFieldType(schemaProp: JSONSchemaProperty): ConfigurationProperty['type'] {
  if (schemaProp.enum) {
    return 'select'
  }

  switch (schemaProp.type) {
    case 'boolean':
      return 'boolean'
    case 'number':
    case 'integer':
      return 'number'
    case 'string':
      // Check if it's a duration format based on default value pattern (HH:MM:SS)
      if (schemaProp.default && typeof schemaProp.default === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(schemaProp.default)) {
        return 'duration'
      }
      // Check if title or description suggests it's a duration
      const titleLower = schemaProp.title?.toLowerCase() || ''
      const descLower = schemaProp.description?.toLowerCase() || ''
      if (titleLower.includes('interval') || titleLower.includes('duration') ||
          descLower.includes('hh:mm:ss') || descLower.includes('(hh:mm:ss)')) {
        return 'duration'
      }
      // Check if title suggests it's a phone number
      if (titleLower.includes('number') && !titleLower.includes('maximum') && !titleLower.includes('retry')) {
        return 'tel'
      }
      // Check if title suggests it's a password
      if (titleLower.includes('password')) {
        return 'password'
      }
      return 'text'
    default:
      return 'text'
  }
}

// Parse JSON schema into UI-friendly layout format
export function parseSchemaToLayout(schemaConfig: DeviceConfigurationSchema): DeviceConfigurationLayout {
  const sections: ConfigurationSection[] = []

  for (const fieldset of schemaConfig.form) {
    const properties: ConfigurationProperty[] = []

    for (const itemPath of fieldset.items) {
      const schemaProp = getSchemaProperty(schemaConfig.schema, itemPath)

      if (!schemaProp) {
        console.warn(`Schema property not found for path: ${itemPath}`)
        continue
      }

      const property: ConfigurationProperty = {
        name: schemaProp.title || itemPath.split('.').pop() || itemPath,
        key: itemPath,
        type: getFieldType(schemaProp),
        description: schemaProp.description
      }

      // Add min/max for numbers
      if (property.type === 'number' && schemaProp.minimum !== undefined && schemaProp.maximum !== undefined) {
        property.min = schemaProp.minimum
        property.max = schemaProp.maximum
      }

      // Add options for select fields
      if (property.type === 'select' && schemaProp.enum) {
        property.options = schemaProp.enum
      }

      properties.push(property)
    }

    sections.push({
      title: fieldset.title,
      properties
    })
  }

  return { sections }
}

// Extract default values from schema
export function extractDefaultValues(schemaConfig: DeviceConfigurationSchema): DeviceConfigurationValues {
  const defaults: DeviceConfigurationValues = {}

  function traverseSchema(obj: any, path: string = '') {
    if (obj.properties) {
      for (const [key, value] of Object.entries(obj.properties)) {
        const currentPath = path ? `${path}.${key}` : key
        const prop = value as JSONSchemaProperty | JSONSchemaObject

        if ('default' in prop && prop.default !== undefined) {
          defaults[currentPath] = prop.default
        }

        if ('properties' in prop) {
          traverseSchema(prop, currentPath)
        }
      }
    }
  }

  traverseSchema(schemaConfig.schema)
  return defaults
}

// Get value from nested object using dot notation path
export function getNestedValue(obj: any, path: string): any {
  const parts = path.split('.')
  let current = obj

  for (const part of parts) {
    if (current === undefined || current === null) {
      return undefined
    }
    current = current[part]
  }

  return current
}

// Set value in nested object using dot notation path
export function setNestedValue(obj: any, path: string, value: any): any {
  const parts = path.split('.')
  const result = { ...obj }
  let current = result

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]
    if (!current[part]) {
      current[part] = {}
    } else {
      current[part] = { ...current[part] }
    }
    current = current[part]
  }

  current[parts[parts.length - 1]] = value
  return result
}

// Convert flat configuration values to nested structure for API
export function flatToNested(flatValues: DeviceConfigurationValues): any {
  const result: any = {}

  for (const [key, value] of Object.entries(flatValues)) {
    const parts = key.split('.')
    let current = result

    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i]
      if (!current[part]) {
        current[part] = {}
      }
      current = current[part]
    }

    current[parts[parts.length - 1]] = value
  }

  return result
}

// Convert nested configuration structure from API to flat values
export function nestedToFlat(nested: any, prefix: string = ''): DeviceConfigurationValues {
  const result: DeviceConfigurationValues = {}

  function traverse(obj: any, currentPrefix: string) {
    for (const [key, value] of Object.entries(obj)) {
      const fullKey = currentPrefix ? `${currentPrefix}.${key}` : key

      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        traverse(value, fullKey)
      } else {
        result[fullKey] = value as string | number | boolean | null
      }
    }
  }

  traverse(nested, prefix)
  return result
}
